import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const NAMES = { boho: "Boho Picnic", floating: "Floating Picnic", electronic: "Picnic Electronic" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filters, setFilters] = useState({ experience: "", date: "", payment_status: "" });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    const q = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join("&");
    api.get(`/admin/bookings${q ? `?${q}` : ""}`).then((r) => setBookings(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Réservation mise à jour");
      load();
      if (selected?.id === id) setSelected({ ...selected, ...patch });
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const badge = (status) => status === "paid"
    ? "bg-olive/15 text-olive-deep" : status === "refunded"
    ? "bg-burgundy/10 text-burgundy" : "bg-clay/10 text-clay";

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-serif text-3xl sm:text-4xl text-ink">Réservations</h1>
      <div className="w-16 h-px bg-gold mt-4" />
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-8">
        <select data-testid="filter-experience" className="input-elegant sm:!w-auto !py-2.5 text-sm" value={filters.experience}
          onChange={(e) => setFilters({ ...filters, experience: e.target.value })}>
          <option value="">Toutes les expériences</option>
          <option value="boho">Boho Picnic</option>
          <option value="floating">Floating Picnic</option>
          <option value="electronic">Picnic Electronic</option>
        </select>
        <input data-testid="filter-date" type="date" className="input-elegant sm:!w-auto !py-2.5 text-sm" value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })} />
        <select data-testid="filter-payment" className="input-elegant sm:!w-auto !py-2.5 text-sm" value={filters.payment_status}
          onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
          <option value="">Tous paiements</option>
          <option value="pending">En attente</option>
          <option value="paid">Payé</option>
          <option value="refunded">Remboursé</option>
        </select>
      </div>

      <div className="mt-6 bg-ivory border border-champagne rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="bookings-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.12em] text-earth border-b border-champagne">
              <th className="px-5 py-4">Client</th><th className="px-5 py-4">Expérience</th>
              <th className="px-5 py-4">Date & heure</th><th className="px-5 py-4">Invités</th>
              <th className="px-5 py-4">Montant</th><th className="px-5 py-4">Paiement</th><th className="px-5 py-4">Statut</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id.slice(0, 8)}`} onClick={() => { setSelected(b); setNotes(b.internal_notes || ""); }}
                className="border-b border-champagne/50 last:border-0 cursor-pointer hover:bg-cream/60 transition-colors">
                <td className="px-5 py-4"><span className="text-ink">{b.customer.name}</span>
                  <span className="block text-xs text-earth">{b.customer.email}</span></td>
                <td className="px-5 py-4 text-earth">{NAMES[b.experience]}{b.package ? ` (${b.package})` : ""}</td>
                <td className="px-5 py-4 text-earth">{b.date} · {b.time_slot}</td>
                <td className="px-5 py-4 text-earth">{b.guests}</td>
                <td className="px-5 py-4 text-ink">{b.amount.toFixed(0)} €</td>
                <td className="px-5 py-4"><span className={`text-xs uppercase tracking-wide px-3 py-1 rounded-full ${badge(b.payment_status)}`}>{b.payment_status}</span></td>
                <td className="px-5 py-4 text-earth">{b.status}</td>
              </tr>
            ))}
            {bookings.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-earth">Aucune réservation.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div data-testid="booking-detail-panel" className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={() => setSelected(null)}>
          <div className="w-full max-w-md bg-ivory h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl text-ink">{selected.customer.name}</h2>
            <p className="text-sm text-earth mt-1">{selected.customer.email} · {selected.customer.phone}</p>
            <div className="mt-6 space-y-2 text-sm text-earth">
              <p><span className="text-ink font-medium">Expérience :</span> {NAMES[selected.experience]}{selected.package ? ` — ${selected.package}` : ""}</p>
              {selected.event_title && <p><span className="text-ink font-medium">Événement :</span> {selected.event_title}</p>}
              <p><span className="text-ink font-medium">Date :</span> {selected.date} · {selected.time_slot}</p>
              <p><span className="text-ink font-medium">Invités :</span> {selected.guests}</p>
              <p><span className="text-ink font-medium">Montant :</span> {selected.amount.toFixed(2)} €</p>
              <p><span className="text-ink font-medium">Référence :</span> {selected.id}</p>
            </div>
            {selected.requests && (
              <div className="mt-5 bg-cream border border-champagne rounded-2xl p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-earth mb-2">Demandes du client</p>
                <p className="text-sm text-ink">{selected.requests}</p>
              </div>
            )}
            <div className="mt-6 space-y-3">
              <label className="block text-xs uppercase tracking-[0.15em] text-earth">Paiement</label>
              <div className="flex gap-2">
                {["pending", "paid", "refunded"].map((s) => (
                  <button key={s} data-testid={`set-payment-${s}`} onClick={() => update(selected.id, { payment_status: s })}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-wide border transition-colors ${selected.payment_status === s ? "bg-terracotta text-ivory border-terracotta" : "border-champagne text-earth hover:border-terracotta"}`}>
                    {s === "pending" ? "En attente" : s === "paid" ? "Payé" : "Remboursé"}
                  </button>
                ))}
              </div>
              <label className="block text-xs uppercase tracking-[0.15em] text-earth pt-3">Statut</label>
              <div className="flex gap-2">
                {["reserved", "confirmed", "cancelled"].map((s) => (
                  <button key={s} data-testid={`set-status-${s}`} onClick={() => update(selected.id, { status: s })}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-wide border transition-colors ${selected.status === s ? "bg-ink text-ivory border-ink" : "border-champagne text-earth hover:border-ink"}`}>
                    {s === "reserved" ? "Réservé" : s === "confirmed" ? "Confirmé" : "Annulé"}
                  </button>
                ))}
              </div>
              <label className="block text-xs uppercase tracking-[0.15em] text-earth pt-3">Notes internes</label>
              <textarea data-testid="internal-notes" rows={3} className="input-elegant" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <button data-testid="save-notes-btn" onClick={() => update(selected.id, { internal_notes: notes })} className="btn-primary w-full !py-3">
                Enregistrer les notes
              </button>
              <button data-testid="close-detail-btn" onClick={() => setSelected(null)} className="btn-outline w-full !py-3">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
